import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { BookOpen, ArrowLeft, Users, BarChart3, Clock, FileText, ChevronDown, ChevronUp } from 'lucide-react';

export default function AdminPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedUser, setExpandedUser] = useState(null);
    const [userFeedback, setUserFeedback] = useState({});

    const fetchData = useCallback(async () => {
        try {
            const [usersRes, statsRes] = await Promise.all([
                api.get('/api/admin/users'),
                api.get('/api/admin/stats')
            ]);
            setUsers(usersRes.data);
            setStats(statsRes.data);
        } catch (err) {
            if (err.response?.status === 403) {
                setError('Access denied. Admin only.');
            } else if (err.response?.status === 401) {
                navigate('/login');
                return;
            } else {
                setError('Failed to load admin data.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const toggleUserFeedback = async (userId) => {
        if (expandedUser === userId) {
            setExpandedUser(null);
            return;
        }
        setExpandedUser(userId);
        if (!userFeedback[userId]) {
            try {
                const res = await api.get(`/api/admin/user/${userId}/feedback`);
                setUserFeedback(prev => ({ ...prev, [userId]: res.data }));
            } catch {
                setUserFeedback(prev => ({ ...prev, [userId]: [] }));
            }
        }
    };

    const formatDate = (d) => {
        if (!d) return '—';
        const date = new Date(d);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (loading) return (
        <div className="min-h-screen bg-[#F8F7FC] flex items-center justify-center">
            <div className="skeleton w-64 h-8 rounded-lg" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-[#F8F7FC] flex items-center justify-center">
            <div className="brutalist-card p-8 text-center max-w-md">
                <p className="text-red-600 font-bold mb-2">{error}</p>
                <p className="text-zinc-500 text-sm mb-4">If you just deployed, please wait a moment and refresh. The server may still be starting up.</p>
                <div className="flex gap-3 justify-center">
                    <button onClick={() => { setError(''); setLoading(true); fetchData(); }} className="brutalist-btn-secondary">Retry</button>
                    <button onClick={() => navigate('/dashboard')} className="brutalist-btn-primary">Back to Dashboard</button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#F8F7FC]">
            <header className="border-b-2 border-zinc-950 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="admin-back">
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                                <div className="w-10 h-10 rounded-lg bg-[#A78BFA] border-2 border-zinc-950 flex items-center justify-center">
                                    <BookOpen className="w-5 h-5 text-white" />
                                </div>
                                <h1 className="text-xl font-black tracking-tight">Admin Panel</h1>
                            </a>
                        </div>
                        <span className="text-sm text-zinc-500">{user?.email}</span>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <StatCard icon={<Users className="w-5 h-5" />} label="Total Users" value={stats.total_users} />
                        <StatCard icon={<FileText className="w-5 h-5" />} label="Total Submissions" value={stats.total_feedback} />
                        <StatCard icon={<Users className="w-5 h-5" />} label="Signups (7d)" value={stats.recent_signups_7d} />
                        <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Submissions (7d)" value={stats.recent_feedback_7d} />
                    </div>
                )}

                {/* Subject breakdown */}
                {stats?.feedback_by_subject && Object.keys(stats.feedback_by_subject).length > 0 && (
                    <div className="brutalist-card p-6 mb-8">
                        <h3 className="text-sm font-bold text-zinc-500 mb-3">Submissions by Subject</h3>
                        <div className="flex flex-wrap gap-3">
                            {Object.entries(stats.feedback_by_subject).map(([subject, count]) => (
                                <span key={subject} className="px-3 py-1.5 bg-[#EDE9FE] border-2 border-zinc-950 rounded-lg text-sm font-semibold">
                                    {subject}: {count}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Users Table */}
                <div className="brutalist-card overflow-hidden">
                    <div className="p-6 border-b-2 border-zinc-950">
                        <h2 className="text-lg font-bold">Registered Users ({users.length})</h2>
                    </div>
                    <div className="divide-y divide-zinc-200">
                        {users.map((u) => (
                            <div key={u.id}>
                                <button
                                    onClick={() => toggleUserFeedback(u.id)}
                                    className="w-full text-left px-6 py-4 hover:bg-zinc-50 transition-colors"
                                    data-testid={`admin-user-${u.id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-sm">{u.name}</span>
                                                {u.role === 'admin' && <span className="text-xs bg-[#A78BFA] text-white px-2 py-0.5 rounded-full font-bold">Admin</span>}
                                            </div>
                                            <p className="text-sm text-zinc-500 truncate">{u.email}</p>
                                        </div>
                                        <div className="flex items-center gap-6 text-sm text-zinc-500">
                                            <div className="text-right hidden sm:block">
                                                <p className="font-semibold text-zinc-700">{u.feedback_count} submissions</p>
                                                <p className="text-xs">Joined {formatDate(u.created_at)}</p>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-zinc-400">
                                                {u.last_activity && (
                                                    <span className="hidden md:inline"><Clock className="w-3 h-3 inline" /> Last: {formatDate(u.last_activity)}</span>
                                                )}
                                            </div>
                                            {expandedUser === u.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </div>
                                    </div>
                                </button>

                                {/* Expanded: user's feedback */}
                                {expandedUser === u.id && (
                                    <div className="px-6 pb-4 bg-zinc-50 fade-in">
                                        {!userFeedback[u.id] ? (
                                            <div className="skeleton h-10 w-full rounded-lg" />
                                        ) : userFeedback[u.id].length === 0 ? (
                                            <p className="text-sm text-zinc-400 py-2">No submissions yet</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {userFeedback[u.id].map((fb) => (
                                                    <div key={fb.id} className="flex items-center justify-between bg-white border border-zinc-200 rounded-lg px-4 py-2.5">
                                                        <div className="flex-1 min-w-0">
                                                            <span className="text-xs font-semibold text-[#A78BFA]">{fb.subject}</span>
                                                            {fb.source === 'create' && <span className="text-xs bg-[#EDE9FE] text-[#A78BFA] px-1.5 py-0.5 rounded ml-2 font-bold">Create</span>}
                                                            <p className="text-sm text-zinc-700 truncate">{fb.question}</p>
                                                        </div>
                                                        <div className="text-right ml-4 flex-shrink-0">
                                                            <span className="font-bold text-sm">{fb.score}/{fb.max_score}</span>
                                                            <p className="text-xs text-zinc-400">{formatDate(fb.created_at)}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}

function StatCard({ icon, label, value }) {
    return (
        <div className="brutalist-card p-4">
            <div className="flex items-center gap-2 text-zinc-500 mb-1">
                {icon}
                <span className="text-xs font-semibold">{label}</span>
            </div>
            <p className="text-2xl font-black">{value}</p>
        </div>
    );
}
